import { AxiosInstance } from 'axios';
import {
    IFollowUpStatusResponse,
    IMyNetworkScorePendingFollowUpsResponse,
    IMyNetworkScoreSummaryResponse,
    INetworkScoreHabitsResponse,
    INetworkScoreHistoryResponse,
    INetworkScoreLessonResponse,
    INetworkScoreRule,
    IUnacknowledgedLevelUpResponse,
} from '../interfaces/INetworkScore';

/**
 * Fetches the signed-in user's Network Score summary and pending follow-up actions
 * (docs/features/future-roadmap/network-score.md, section 11).
 */
export class NetworkScoreService {
    private readonly apiClient: AxiosInstance;

    constructor(apiClient: AxiosInstance) {
        this.apiClient = apiClient;
    }

    async getMySummary(): Promise<IMyNetworkScoreSummaryResponse> {
        const response = await this.apiClient.get<IMyNetworkScoreSummaryResponse>('/participant/score/me');
        return response.data;
    }

    async getMyPendingFollowUps(take?: number): Promise<IMyNetworkScorePendingFollowUpsResponse> {
        const response = await this.apiClient.get<IMyNetworkScorePendingFollowUpsResponse>('/participant/score/me/pending-follow-ups', {
            params: take ? { take } : undefined,
        });
        if (!Array.isArray(response.data?.pendingFollowUpResponses) ||
            !Array.isArray(response.data?.pendingReconnectResponses)) {
            throw new Error('Invalid pending follow-ups response: expected follow-up and reconnect lists.');
        }
        return response.data;
    }

    /** The signed-in user's first-follow-up state with one connection, for the profile page panel. */
    async getFollowUpStatus(otherUserId: string): Promise<IFollowUpStatusResponse> {
        const response = await this.apiClient.get<IFollowUpStatusResponse>(`/participant/score/me/follow-ups/${encodeURIComponent(otherUserId)}`);
        return response.data;
    }

    /** Marks a level-up as acknowledged so the Level-up dialog doesn't show it again. */
    async acknowledgeLevelUp(toLevelName: string): Promise<void> {
        await this.apiClient.put(`/participant/score/me/level-ups/${encodeURIComponent(toLevelName)}/acknowledge`);
    }

    async getUnacknowledgedLevelUp(): Promise<IUnacknowledgedLevelUpResponse | null> {
        const response = await this.apiClient.get<IUnacknowledgedLevelUpResponse>('/participant/score/me/level-up');
        return response.status === 204 ? null : response.data;
    }

    /** The five habits for "Your habits" on the own-profile Network score tab. */
    async getMyHabits(): Promise<INetworkScoreHabitsResponse> {
        const response = await this.apiClient.get<INetworkScoreHabitsResponse>('/participant/score/me/habits');
        return response.data;
    }

    /** The lesson unlocked at the user's current level, or null when that level has none. */
    async getMyLesson(): Promise<INetworkScoreLessonResponse | null> {
        const response = await this.apiClient.get<INetworkScoreLessonResponse>('/participant/score/me/lesson');
        return response.status === 204 || !response.data ? null : response.data;
    }

    /** One page of the user's score events, newest first. */
    async getMyHistory(page: number, pageSize: number): Promise<INetworkScoreHistoryResponse> {
        const response = await this.apiClient.get<INetworkScoreHistoryResponse>('/participant/score/me/history', {
            params: { page, pageSize },
        });
        return response.data;
    }

    /** The active score rules for "How you earn points". */
    async getScoreRules(): Promise<INetworkScoreRule[]> {
        const response = await this.apiClient.get<INetworkScoreRule[]>('/participant/score/rules');
        return response.data;
    }
}
